import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import cn from 'classnames';
import styles from './FullModalView.module.scss';
import type { MediaGalleryConfig } from '../../MediaGallery.config.ts';
import type {
  GalleryEventSourceType,
  MediaGalleryProps
} from '../../MediaGallery.tsx';
import {
  getCurrentMediaItemByIndex,
  getNextNavigatableItemAndIndex
} from '../../MediaGallery.utils.ts';
import Thumbnails from '../Thumbnails/Thumbnails.tsx';
import CloseButton from '../CloseButton/CloseButton.tsx';
import ZoomImage from '../ZoomImage/ZoomImage.tsx';

export type FullModalViewProps = {
  isOpen: boolean;
  media: MediaGalleryProps['media'];
  currentIndex: number;
  config: MediaGalleryConfig;
  onThumbnailClick: (
    event: JSX.TargetedEvent<HTMLButtonElement>,
    clickedIndex: number
  ) => void;
  onClose: (
    event?: JSX.TargetedEvent<HTMLDialogElement | HTMLButtonElement, Event>,
    newIndex?: number
  ) => void;
  onNextPrev: (
    event: JSX.TargetedEvent<HTMLButtonElement>,
    clickedIndex: number
  ) => void;
  /** Triggered when the user views an image in the gallery (not magnified) */
  onGalleryImageViewed?: (
    newIndex: number,
    eventSource: GalleryEventSourceType
  ) => void;
  /** Triggered when the user views a magnified image in the gallery */
  onMagnifiedImageViewed?: (newIndex: number) => void;
};

export default function FullModalView({
  isOpen = false,
  media,
  currentIndex,
  config,
  onThumbnailClick,
  onNextPrev,
  onClose = () => {},
  onMagnifiedImageViewed = () => {},
  onGalleryImageViewed = () => {}
}: FullModalViewProps) {
  const { items, thumbnails = items, hires } = media;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { translations: t } = config;
  const [isClosing, setIsClosing] = useState<boolean>(false);

  const [isZoomed, setIsZoomed] = useState<boolean>(false);
  const currentMediaItem = getCurrentMediaItemByIndex(
    items ?? [],
    currentIndex
  );
  if (!currentMediaItem) {
    return null;
  }
  const currentHiresItem = hires?.length
    ? getCurrentMediaItemByIndex(hires ?? [], currentIndex)
    : undefined;

  const hideModal = () => {
    setIsClosing(true);

    // Have to manually close after animation due to Safari
    const close = () => {
      setIsClosing(false);
      dialogRef.current?.close();
      dialogRef.current?.removeEventListener('animationend', close);
    };
    dialogRef.current?.addEventListener('animationend', close);
  };

  const onDialogClick = (event: MouseEvent) => {
    if (event.target === dialogRef.current) {
      setIsZoomed(false);
      onClose();
    }
  };

  useEffect(() => {
    if (!dialogRef.current) return undefined;
    dialogRef.current?.addEventListener('click', onDialogClick);
    return () => {
      dialogRef.current?.removeEventListener('click', onDialogClick);
    };
  }, [dialogRef.current]);

  useEffect(() => {
    if (!dialogRef.current) return;

    if (isOpen) {
      dialogRef.current.showModal();
    } else if (dialogRef.current.open) {
      hideModal();
    }
  }, [dialogRef.current, isOpen]);

  const advanceSlideBy = (
    event: JSX.TargetedEvent<HTMLButtonElement>,
    direction: 1 | -1
  ) => {
    event.preventDefault();
    // reset the zoom for the new image
    setIsZoomed(false);

    const { newIndex, newItem } = getNextNavigatableItemAndIndex(
      items ?? [],
      currentIndex,
      direction
    );
    if (newItem) {
      onGalleryImageViewed(newIndex, 'chevron');
      onNextPrev(event, newIndex);
    }
  };

  const handleThumbnailClick = (
    event: JSX.TargetedEvent<HTMLButtonElement>,
    clickedIndex: number
  ) => {
    if (clickedIndex === currentIndex) return; // do nothing if already on that image
    // reset the zoom for the new image
    setIsZoomed(false);
    // register a new image view event
    onGalleryImageViewed(clickedIndex, 'thumbnail');

    onThumbnailClick(event, clickedIndex);
  };

  const handleMagnifyClick = () => {
    setIsZoomed(!isZoomed);
  };

  const handleMagnifyEvent = () => {
    onMagnifiedImageViewed(currentIndex);
  };

  const handleImageLoadEvent = () => {
    onGalleryImageViewed(currentIndex, 'default');
  };

  const handleCloseClick = (event: JSX.TargetedEvent<HTMLButtonElement>) => {
    // reset zoom on close
    setIsZoomed(false);
    onClose(event);
  };

  /** Disable zooming for videos */
  const supportsZoom = currentHiresItem && currentMediaItem.type !== 'VIDEO';

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      class={cn(styles.container, {
        [styles.isClosing]: isClosing
      })}
    >
      {(isOpen || isClosing) && (
        <div class={styles.galleryInner}>
          <div class={cn(styles.content, { [styles.contentZoomed]: isZoomed })}>
            <ZoomImage
              config={config}
              advanceSlideBy={advanceSlideBy}
              onClick={handleMagnifyClick}
              onMagnify={handleMagnifyEvent}
              onImageLoad={handleImageLoadEvent}
              currentIndex={currentIndex}
              items={items}
              item={currentMediaItem}
              hiresImage={currentHiresItem}
              isZoomed={isZoomed && !!supportsZoom}
              isClosing={isClosing}
            />

            <div class={styles.thumbsContainer}>
              <Thumbnails
                config={config}
                isFullscreen
                items={thumbnails}
                selectedIndex={currentIndex}
                onThumbnailClick={(e, clickedIndex) =>
                  handleThumbnailClick(e, clickedIndex)
                }
              />
            </div>
          </div>

          <CloseButton
            ariaLabel={t.closeLabel}
            onClose={handleCloseClick}
            class={styles.closeButton}
          />
        </div>
      )}
    </dialog>
  );
}
